import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import set from 'ember-set-helper/helpers/set';
import {
  HdsTextBody,
  HdsModal,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { ModalConfirm, Loading } from 'core/components';
import { boxMargin } from 'core/modifiers';
import { FormValidationError } from 'core/utils/form-validation';
import { GEOS } from 'core/utils/constants';
import AlertBanner from 'hcp/components/page/account-settings/security/alert-banner';

export default class ManageMfaDisable extends Component {
  @service api;
  @service config;
  @service flashMessages;
  @service intl;
  @service mfa;

  @tracked allowDisable = false;

  REGION_US = GEOS.US;

  get deployGeo() {
    return this.config?.app?.deployGeo ?? GEOS.US;
  }

  redirectForDisableMFAToken = task(
    {
      drop: true,
    },
    async (evt) => {
      evt.preventDefault();
      if (this.allowDisable === false) {
        //TODO fix this and show it in ModalConfirm
        throw new FormValidationError(`Please enter DISABLE to disable MFA`);
      }
      let mfaToken;
      try {
        mfaToken = await this.mfa.getMFAToken(this.args.onCancel);
        await this.api.profile.profileServiceDisableMFA({
          mfaToken,
        });
        this.flashMessages.success(
          this.intl.t(
            'components.page.account-settings.security.mfaDisable.flashMessage.success'
          )
        );
        this.args.onStatusChange();
      } catch {
        // if there's a token it was disable that errored
        // otherwise show an error for token generation
        let messageKey = mfaToken
          ? 'components.page.account-settings.security.mfaDisable.flashMessage.error'
          : 'components.page.account-settings.security.mfa.tokenError';

        this.flashMessages.error(this.intl.t(messageKey));
        this.args.onCancel();
      }
    }
  );
  <template>
    <HdsModal
      @onClose={{@onCancel}}
      @color="critical"
      @isDismissDisabled={{this.redirectForDisableMFAToken.isRunning}}
      as |M|
    >
      <M.Header @icon="alert-diamond" data-test-mfa-modalHeader>
        {{t
          "components.page.account-settings.security.mfaDisable.modal-heading"
        }}
      </M.Header>
      <M.Body ...attributes>
        {{#if this.redirectForDisableMFAToken.isRunning}}
          <Loading data-test-mfa-loading as |L|>
            <L.Message>
              {{t "components.page.account-settings.security.waitForPopup"}}
            </L.Message>
          </Loading>
        {{else}}
          {{#if (eq this.deployGeo this.REGION_US)}}
            <AlertBanner
              data-test-mfa-enabled-warning
              @header={{t
                "components.page.account-settings.security.banner.heading.mfa"
              }}
              {{boxMargin "0 0 md 0"}}
            />
          {{/if}}
          <HdsTextBody @tag="p" {{boxMargin "0 0 sm 0"}}>
            {{t "components.page.account-settings.security.mfaDisable.body"}}
          </HdsTextBody>
          <HdsTextBody @tag="p" @weight="semibold" {{boxMargin "0 0 lg 0"}}>
            {{t "components.page.account-settings.security.mfaDisable.notice"}}
          </HdsTextBody>
          <form
            id="mfa-disable"
            {{on "submit" this.redirectForDisableMFAToken.perform}}
          >
            <ModalConfirm
              @onAllowedChange={{set this "allowDisable"}}
              @matchString={{t
                "components.page.account-settings.security.mfaDisable.fieldMatchString"
              }}
            />
          </form>
        {{/if}}
      </M.Body>
      {{#unless this.redirectForDisableMFAToken.isRunning}}
        <M.Footer as |F|>
          <HdsButtonSet>
            <HdsButton
              @color="critical"
              @text={{t
                "components.page.account-settings.security.mfaDisable.confirmBtn"
              }}
              type="submit"
              form="mfa-disable"
              data-test-mfa-disable
            />
            <HdsButton
              @color="secondary"
              @text={{t "components.form.cancel"}}
              {{on "click" F.close}}
            />
          </HdsButtonSet>
        </M.Footer>
      {{/unless}}
    </HdsModal>
  </template>
}
